import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../../common/app';
import Component from '../Component';
import listItems from '../helpers/listItems';
import extractText from '../utils/extractText';
import Tooltip from './Tooltip';
import Icon from './Icon';
/**
 * The `Dropdown` component displays a button which, when clicked, shows a
 * dropdown menu beneath it.
 *
 * The children will be displayed as a list inside the dropdown menu.
 */
export default class Dropdown extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "showing", false);
    _defineProperty(this, "closeWatcher", void 0);
  }
  static initAttrs(attrs) {
    var _attrs$caretIcon;
    attrs.className || (attrs.className = '');
    attrs.buttonClassName || (attrs.buttonClassName = '');
    attrs.menuClassName || (attrs.menuClassName = '');
    attrs.label || (attrs.label = '');
    (_attrs$caretIcon = attrs.caretIcon) != null ? _attrs$caretIcon : attrs.caretIcon = 'fas fa-caret-down';
    attrs.accessibleToggleLabel || (attrs.accessibleToggleLabel = extractText(app.translator.trans('core.lib.dropdown.toggle_dropdown_accessible_label')));
  }
  view(vnode) {
    const items = vnode.children ? listItems(vnode.children) : [];
    const renderItems = this.attrs.lazyDraw ? this.showing : true;
    return m("div", {
      className: 'ButtonGroup Dropdown dropdown ' + this.attrs.className + ' itemCount' + items.length + (this.showing ? ' open' : '')
    }, this.getButton(vnode.children), renderItems && this.getMenu(items));
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // When opening the dropdown menu, work out if the menu goes beyond the
    // bottom of the viewport. If it does, we will apply class to make it show
    // above the toggle button instead of below it.
    this.$().on('shown.bs.dropdown', () => {
      var _$menu$offset$top, _$menu$offset, _$menu$height, _$$scrollTop, _$$height, _$menu$offset2, _$menu$offset$left, _$menu$offset3, _$menu$width, _$$scrollLeft, _$$width;
      const {
        lazyDraw,
        onshow
      } = this.attrs;
      this.showing = true;

      // If using lazy drawing, redraw before calling `onshow` function
      // to make sure the menu DOM exists in case the callback tries to use it.
      if (lazyDraw) {
        m.redraw.sync();
      }
      if (typeof onshow === 'function') {
        onshow();
      }

      // If not using lazy drawing, keep previous functionality
      // of redrawing after calling onshow()
      if (!lazyDraw) {
        m.redraw();
      }
      const $menu = this.$('.Dropdown-menu');
      const isRight = $menu.hasClass('Dropdown-menu--right');
      const top = (_$menu$offset$top = (_$menu$offset = $menu.offset()) == null ? void 0 : _$menu$offset.top) != null ? _$menu$offset$top : 0;
      const height = (_$menu$height = $menu.height()) != null ? _$menu$height : 0;
      const windowScrollTop = (_$$scrollTop = $(window).scrollTop()) != null ? _$$scrollTop : 0;
      const windowHeight = (_$$height = $(window).height()) != null ? _$$height : 0;
      $menu.removeClass('Dropdown-menu--top Dropdown-menu--right');
      $menu.toggleClass('Dropdown-menu--top', top + height > windowScrollTop + windowHeight);
      if ((((_$menu$offset2 = $menu.offset()) == null ? void 0 : _$menu$offset2.top) || 0) < 0) {
        $menu.removeClass('Dropdown-menu--top');
      }
      const left = (_$menu$offset$left = (_$menu$offset3 = $menu.offset()) == null ? void 0 : _$menu$offset3.left) != null ? _$menu$offset$left : 0;
      const width = (_$menu$width = $menu.width()) != null ? _$menu$width : 0;
      const windowScrollLeft = (_$$scrollLeft = $(window).scrollLeft()) != null ? _$$scrollLeft : 0;
      const windowWidth = (_$$width = $(window).width()) != null ? _$$width : 0;
      $menu.toggleClass('Dropdown-menu--right', isRight || left + width > windowScrollLeft + windowWidth);
      if ('CloseWatcher' in window) {
        var _this$closeWatcher;
        (_this$closeWatcher = this.closeWatcher) == null || _this$closeWatcher.destroy();
        this.closeWatcher = new CloseWatcher();
        this.closeWatcher.onclose = () => {
          // @ts-ignore - missing dropdown types
          this.$('.Dropdown-toggle').dropdown('toggle');
        };
      }
    });
    this.$().on('hidden.bs.dropdown', () => {
      var _this$closeWatcher2;
      this.showing = false;
      (_this$closeWatcher2 = this.closeWatcher) == null || _this$closeWatcher2.destroy();
      this.closeWatcher = undefined;
      if (this.attrs.onhide) {
        this.attrs.onhide();
      }
      m.redraw();
    });

    // Safari fix: Only close on focusout if relatedTarget exists and is outside.
    // In Safari, clicks on menu items trigger focusout with relatedTarget=null,
    // which was causing premature dropdown closure. Bootstrap handles click-outside,
    // so we only need to handle keyboard navigation (tab) away from the dropdown.
    this.$().on('focusout', e => {
      // If relatedTarget is null, it's likely a click event - let Bootstrap handle it
      if (!e.relatedTarget) {
        return;
      }

      // Check if the new focused element is outside of this dropdown
      if (!this.$().has(e.relatedTarget).length) {
        this.$().trigger('hidden.bs.dropdown');
      }
    });
  }

  /**
   * Get the template for the button.
   */
  getButton(children) {
    let button = m("button", Object.assign({
      type: "button",
      className: 'Dropdown-toggle ' + this.attrs.buttonClassName,
      "aria-haspopup": "menu",
      "aria-label": this.attrs.accessibleToggleLabel,
      "data-toggle": "dropdown",
      onclick: this.attrs.onclick
    }, this.attrs.buttonAttrs), this.getButtonContent(children));
    if (this.attrs.tooltip) {
      button = m(Tooltip, {
        text: this.attrs.tooltip,
        position: "bottom"
      }, button);
    }
    return button;
  }

  /**
   * Get the template for the button's content.
   */
  getButtonContent(children) {
    return [this.attrs.icon ? m(Icon, {
      name: this.attrs.icon,
      className: "Button-icon"
    }) : '', m("span", {
      className: "Button-label"
    }, m("span", {
      className: "Button-labelText"
    }, this.attrs.label), this.getButtonSubContent()), this.attrs.caretIcon ? m(Icon, {
      name: this.attrs.caretIcon,
      className: "Button-caret"
    }) : ''];
  }
  getButtonSubContent() {
    return this.attrs.helperText ? m("span", {
      className: "Button-helperText"
    }, this.attrs.helperText) : null;
  }
  getMenu(items) {
    return m("ul", {
      className: 'Dropdown-menu dropdown-menu ' + this.attrs.menuClassName
    }, items);
  }
}
flarum.reg.add('core', 'common/components/Dropdown', Dropdown);